import { Heading, Inline, Stack, vars } from '@etg/wings';
import { css } from '@eti/styles';
import { date, duration as durationUtil } from '@eti/utils';
import { Circle, Clock } from '@phosphor-icons/react';
import { useDateFormat, useSiteContext, useTranslation } from '@eti/providers';
import { EventSegmentType } from '@eti/schema-types';
import TimelineWrapper from '../../../common/TimelineWrapper';
import TimelineRow from '../common/TimelineRow';
import type { ExtendedEventSegmentType } from '../models/models';
import TripEventBadge from '../../../../../../widgets/trip-event/TripEventBadge';
import ItineraryTripEventContent from './ItineraryTripEventContent';
import ItineraryChangeOfDate from './ItineraryChangeOfDate';

const circleStyles = css`
  circle {
    stroke-width: 60;
  }
`;

const timelineRowStyles = (durationInDays: number) => css`
  background-color: #fafafa;
  border-radius: ${vars.card.borderRadius} ${vars.card.borderRadius}
    ${durationInDays > 0 ? `0 0` : `${vars.card.borderRadius} ${vars.card.borderRadius}`};
  padding-inline-end: 8px;
`;

interface ItineraryEventSegmentProps {
  dataTestId: string;
  segment: ExtendedEventSegmentType;
}

const ItineraryEventSegment = ({ dataTestId, segment }: ItineraryEventSegmentProps) => {
  const { t } = useTranslation();
  const { weekday } = useDateFormat();
  const { language, timeFormat } = useSiteContext();

  const arrivalDate = date.format(segment.nextDeparture, weekday);

  if (!segment.previousArrival || !segment.nextDeparture) {
    return null;
  }

  const durationInDays = date.durationInDays(
    new Date(segment.previousArrival),
    new Date(segment.nextDeparture),
  );

  return (
    <div data-testid={`itinerary-event-segment-${segment.segmentId}`}>
      <TimelineRow className={timelineRowStyles(durationInDays)}>
        <div />
        <TimelineWrapper
          borderColorAfter={vars.colors.inputs.border}
          borderColorBefore={vars.colors.inputs.border}
          borderStyleAfter="dashed"
          borderStyleBefore="dashed"
          childSize={8}
        >
          <Circle className={circleStyles} color="#737373" height={8} width={20} />
        </TimelineWrapper>
        <Stack data-testid={dataTestId}>
          <Inline noWrap spacing={8}>
            <Clock data-testid="stop-icon" size={20} />
            <Heading data-testid="trip-event-STOP-title" level={4}>
              {t(
                'CartTripDetails.Stop',
                durationUtil.getDuration(segment.duration ?? 0, language.code, timeFormat),
                segment.previousDestination,
              )}
            </Heading>
          </Inline>
          {segment.types?.map(
            (type) =>
              type &&
              type !== EventSegmentType.Stop && (
                <TripEventBadge
                  content={<ItineraryTripEventContent segment={segment} type={type} />}
                  hasTitle
                  key={type}
                  type={type}
                />
              ),
          )}
        </Stack>
      </TimelineRow>
      {durationInDays > 0 && (
        <ItineraryChangeOfDate
          arrivalDate={arrivalDate}
          durationInDays={durationInDays}
          isDashed
          isDisplayedLast
          originalArrivalDate={null}
        />
      )}
    </div>
  );
};

export default ItineraryEventSegment;
